import React from 'react';
import {
  StyledTableContainer,
  StyledTableCell,
  StyledBoxLinearProgress,
  StyledTableRow,
  StyledSelectColumn,
} from './index.style';
import { Avatar, LinearProgress, Paper as MuiPaper, MenuItem, Stack, Menu, CircularProgress } from '@mui/material';
import Box from 'component/layoutComponents/Box';
import Typography from 'component/MarketPlace/Typography';
import { Button } from 'component/MarketPlace/ButtonComponents';
import { IconButton } from 'component/MarketPlace/ButtonComponents';
import TaskProgress from '../TaskProgress';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { getAvatarName } from 'utils/functions';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { ActOnBehalfIcon } from 'component/layoutComponents/overlay/overlay';
import { useTheme } from 'styled-components';
import DefaultUptimeTheme from 'component/theme/defaultTheme';

export interface ILeaverCard {
  content?: any;
  doneList?: any;
  loadingTask?: boolean;
  isChangeColor?: any;
  getDateProgress?: any;
  setShowDialog?: any;
  total?: number;
  locale: any;
  handleSubmitActOnBehalf: (leaverInfo: any) => void;
  handleClickCancelOffboarding: () => any;
  handleClickShowAssignedTasks: () => any;
};

export type TaskStatus = 'pending' | 'completed'

export type LeaverTask = {
  id?: string;
  joiner_id?: string;
  offboarding_task_id: string;
  status: TaskStatus;
  completed?: string;
  name?: string;
  description?: string;
  url?: string;
  file_type?: string;
  img?: string;
  active?: boolean;
  picture?: string;
};

type MenuContentProps = {
  handleClickUpdateLastAccessDate: () => void;
  locale?: any;
  handleClickCancelOffboarding: () => void;
  handleClickShowAssignedTasks: () => void;
};

const OffboardingSelectMenuItems = ({ handleClickUpdateLastAccessDate, locale, handleClickCancelOffboarding, handleClickShowAssignedTasks }: { handleClickUpdateLastAccessDate: () => void; locale: any; handleClickCancelOffboarding : () => void; handleClickShowAssignedTasks : () => void}) => {
  const theme = useTheme() as typeof DefaultUptimeTheme;
  
  return (
    <Box>
      <MenuItem onClick={handleClickUpdateLastAccessDate}>
        <Box borderBottom={`1px solid ${theme.uptimeColors.grayScales.lightGray}`} width="100%" padding="5px 0" display="flex" alignItems="center">
          <Typography>{locale?.leaverCard?.menuItem?.updateLastAccessDate}</Typography>
        </Box>
      </MenuItem>
      <MenuItem onClick={handleClickShowAssignedTasks}>
        <Box borderBottom={`1px solid ${theme.uptimeColors.grayScales.lightGray}`} width="100%" padding="5px 0" display="flex" alignItems="center">
          <Typography>{locale?.leaverCard?.menuItem?.assignedUnassignedTasks}</Typography>
        </Box>
      </MenuItem>
      <MenuItem onClick={handleClickCancelOffboarding}>
        <Box width="100%" padding="5px 0" display="flex" alignItems="center">
          <Typography>{locale?.leaverCard?.menuItem?.cancelOffboarding}</Typography>
        </Box>
      </MenuItem>
    </Box>
  );
};



const MenuContent = ({
  handleClickUpdateLastAccessDate,
  locale,
  handleClickCancelOffboarding,
  handleClickShowAssignedTasks
}: MenuContentProps) => {
    return <Stack>
      <OffboardingSelectMenuItems
        handleClickUpdateLastAccessDate={handleClickUpdateLastAccessDate}
        locale={locale}
        handleClickCancelOffboarding={handleClickCancelOffboarding}
        handleClickShowAssignedTasks={handleClickShowAssignedTasks}
      />
    </Stack>
};

const LeaverCard = (props: ILeaverCard) => {
  const { content, doneList, loadingTask, isChangeColor, getDateProgress, setShowDialog, total = 0, locale, handleSubmitActOnBehalf, handleClickCancelOffboarding, handleClickShowAssignedTasks } = props;

  const progress = doneList?.length / total;
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const openActionMenu = Boolean(anchorEl);
  const theme = useTheme() as typeof DefaultUptimeTheme;
  
  const handleCloseActionMenu = () => {
    setAnchorEl(null);
  };

  const handleClickActionMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClickUpdateLastAccessDate = () => {
    setShowDialog(true);
    setAnchorEl(null);
  };

  const handleRenderSubmitActOnBehalf: any = () => {
    return (
      <Button 
        sx={{ width: '100%', fontSize: `${theme.uptimeFontSizes.desktop.fz2}px`, whiteSpace: "nowrap" }}
        startIcon={<ActOnBehalfIcon color={theme.uptimeColors.grayScales.white}/>} 
        size="medium" 
        fullWidth 
        onClick={() => handleSubmitActOnBehalf(content)}
      >
        {(locale?.leaverCard?.returnDeviceOnBehalf)}
      </Button>
    )
  };

  return (
    <MuiPaper
      variant='outlined'
      sx={{
        borderRadius: '8px',
        paddingTop: '16px',
        '&:hover': { boxShadow: "0 1px 2px -2px rgba(0, 0, 0, 0.16),  0 3px 6px 0 rgba(0, 0, 0 , 0.12), 0 5px 12px 4px rgba(0, 0, 0, 0.09) !important" },
      }}
    >
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
        px={4}
      >
        <Box display={{sx: "block" , lg: "flex"}} alignItems="center">
          <Avatar src={content?.avatar} sx={{ width: 70, height: 70, mr: '26px' }}>
            {!content?.avatar && (
              <Typography variant="h4">
              {getAvatarName(content?.full_name || '')}
            </Typography>
            )}
          </Avatar>
          <Box marginRight="26px" minWidth="230px">
            <Typography
              fontSize="22px"
              fontWeight="500"
              lineHeight="normal"
            >
              {content?.full_name}
            </Typography>
            <Typography
              color="text.secondary"
              fontSize="14px"
              fontWeight="400"
              lineHeight="20px"
            >
              {content?.job_title}
            </Typography>
          </Box>
          <StyledBoxLinearProgress isChangeColor={isChangeColor}>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Typography
                color="text.secondary"
                fontSize="14px"
                fontWeight="500"
                lineHeight="20px"
              >
                {locale?.leaverCard?.cardLeaver?.lastdate}:
              </Typography>
              <Typography
                color="primary"
                fontSize="14px"
                fontWeight="500"
                lineHeight="20px"
              >
                {content?.last_working_date}
              </Typography>
            </Box>
            <LinearProgress variant="determinate" value={getDateProgress()} />
          </StyledBoxLinearProgress>
        </Box>
      </Box>

      <Box borderBottom="8px">
        <StyledTableContainer>
          <Table sx={{ minWidth: 650 }} aria-label="simple table">
            <TableHead>
              <StyledTableRow sx={{ height: '32px' }}>
                <StyledTableCell sx={{ width: '200px' }} align="center">
                  <Typography variant="overline" fontWeight="700" color="rgba(0, 0, 0, 0.60)">
                    {locale?.leaverCard?.cardLeaver?.status}
                  </Typography>
                </StyledTableCell>
                <StyledTableCell sx={{ width: '220px' }} align="center">
                  <Typography variant="overline" fontWeight="700" color="rgba(0, 0, 0, 0.60)">
                    {locale?.leaverCard?.cardLeaver?.lastAccessDate}
                  </Typography>
                </StyledTableCell>
                <StyledTableCell sx={{ width: '220px' }} align="center">
                  <Typography variant="overline" fontWeight="700" color="rgba(0, 0, 0, 0.60)">
                    {locale?.leaverCard?.cardLeaver?.offboardingTasks}
                  </Typography>
                </StyledTableCell>
                <StyledTableCell align="center">
                  <Typography variant="overline" fontWeight="700" color="rgba(0, 0, 0, 0.60)">
                    {locale?.leaverCard?.cardLeaver?.managerAction}
                  </Typography>
                </StyledTableCell>
                <StyledTableCell sx={{ width: '70px' }} align="center">
                  <Typography variant="overline" fontWeight="700" color="rgba(0, 0, 0, 0.60)">
                    {locale?.leaverCard?.cardLeaver?.select}
                  </Typography>
                </StyledTableCell>
              </StyledTableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell align="center">
                  {handleRenderSubmitActOnBehalf()}
                </TableCell>
                <TableCell align="center">
                  {content?.last_access_date ||'-'}
                </TableCell>
                <TableCell align="center">
                  {loadingTask ? (
                    <CircularProgress color='primary'/>
                  ) : (
                    <TaskProgress
                    doneList={doneList}
                    total={total}
                    progress={progress}
                    locale={locale}
                  />
                  )} 
                </TableCell>
                <TableCell align="center">
                <a href={`mailto:${content?.email}; ${content?.personal_email}`}>
                  <Button
                    sx={{ width: '100%', fontSize: "12px", whiteSpace: "nowrap" }}
                    endIcon={<ChevronRightIcon />}
                    size="medium"
                    fullWidth
                  >
                    {locale?.leaverCard?.cardLeaver?.btnContact}
                  </Button>
                  </a>
                </TableCell>
                <TableCell align="center">
                  <StyledSelectColumn>
                  <IconButton
                      icon={MoreVertIcon}
                      fontSize="small"
                      size="small"
                      color="inherit"
                      onClick={handleClickActionMenu}
                      aria-label="Action button"
                    />
                    <Menu
                      anchorEl={anchorEl}
                      open={openActionMenu}
                      onClose={handleCloseActionMenu}
                      disableScrollLock
                    >
                      <MenuContent
                        handleClickUpdateLastAccessDate={handleClickUpdateLastAccessDate}
                        locale={locale}
                        handleClickCancelOffboarding={()=>{
                          setAnchorEl(null);
                          handleClickCancelOffboarding();
                        }}
                        handleClickShowAssignedTasks={()=>{
                          setAnchorEl(null);
                          handleClickShowAssignedTasks();
                        }}
                      />
                    </Menu>
                  </StyledSelectColumn> 
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </StyledTableContainer>
      </Box>
    </MuiPaper>
  );
};

export default LeaverCard;
